<script setup>
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CREST_SHAPES, crestColors, crestStyle, crestsFor, defaultOrder, resolveCrest, rotateOrder, swapOrder,
} from '@/lib/crests'

/**
 * The crest worn next to the player's name: its shape, and the order its zones are painted in.
 *
 * The colours are not picked here — they are the paint's own, and a shape asks for as many zones as it
 * has. So only the shapes the current paint can fill are offered: a choice nobody can make is not a
 * choice (see config/crests.php for the list the server accepts).
 */
const props = defineProps({
  /** { shape, order } — the crest to edit; `order` names one colour per zone (1 = bodywork). */
  modelValue: { type: Object, required: true },
  /** The paint's colours in its own order; null until the car's file says how many there are. */
  colors:     { type: Array,  default: null },
  /** The shape ids the server accepts (config/crests.php), which is what it will let the account save. */
  shapes:     { type: Array,  default: () => [] },
  /** The name the preview stands next to. */
  name:       { type: String, default: '' },
  disabled:   { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const { t } = useI18n()

/** Whether the paint's colours are known yet: before that nothing can be offered. */
const known = computed(() => Array.isArray(props.colors))

const crest = computed(() => resolveCrest(props.modelValue, props.colors?.length ?? 1))

/** The shapes offered: those the server knows and the paint has colours for. */
const offered = computed(() => crestsFor(props.colors?.length ?? 0)
  .filter((shape) => props.shapes.includes(shape)))

const zoneColors = computed(() => crestColors(crest.value.order, props.colors ?? []))

const previewStyle = computed(() => crestStyle(crest.value.shape, zoneColors.value))

/** One shape on its own, drawn with the paint's colours in their own order. */
const shapeStyle = (shape) => crestStyle(shape, props.colors ?? [])

function choose(shape)
{
  if (props.disabled) {
    return
  }

  // The arrangement already made is kept, cut to the zones the new shape has.
  const kept = crest.value.order.slice(0, CREST_SHAPES[shape])

  emit('update:modelValue', {
    shape,
    order: kept.length === CREST_SHAPES[shape] ? kept : defaultOrder(CREST_SHAPES[shape]),
  })
}

const reorder = (step) => emit('update:modelValue', { ...crest.value, order: step(crest.value.order) })

/** Whether two crests say the same thing, which is what keeps this watcher from emitting twice. */
function sameCrest(left, right)
{
  return left?.shape === right.shape
    && Array.isArray(left?.order)
    && left.order.length === right.order.length
    && left.order.every((index, position) => index === right.order[position])
}

// A plainer livery can leave the stored crest with more zones than the paint has colours: what the
// page holds becomes the crest that can really be worn, and that is what gets saved.
watch([crest, known], () => {
  if (!known.value || sameCrest(props.modelValue, crest.value)) {
    return
  }

  emit('update:modelValue', crest.value)
}, { immediate: true })
</script>

<template>
  <div v-if="known">
    <!-- the crest as it will stand next to the name: a badge no taller than the line it sits in -->
    <div class="crest-preview text-body-1 mb-5">
      <span class="crest crest--inline" :style="previewStyle" aria-hidden="true"></span>
      <span class="crest-name">{{ name || t('settings.game.crest.previewName') }}</span>
    </div>

    <div class="text-subtitle-2 mb-2">{{ t('settings.game.crest.shape') }}</div>
    <div class="crest-shapes" role="radiogroup" :aria-label="t('settings.game.crest.shape')">
      <button
        v-for="shape in offered"
        :key="shape"
        type="button"
        role="radio"
        class="crest-shape"
        :class="{ selected: crest.shape === shape }"
        :aria-checked="crest.shape === shape"
        :aria-label="t(`settings.game.crest.shapes.${shape}`)"
        :title="t(`settings.game.crest.shapes.${shape}`)"
        :disabled="disabled"
        @click="choose(shape)"
      >
        <span class="crest" :style="shapeStyle(shape)"></span>
      </button>
    </div>

    <p v-if="offered.length === 1" class="text-body-2 text-medium-emphasis mt-3 mb-0">
      {{ t('settings.game.crest.oneColor') }}
    </p>

    <!-- with one colour there is nothing to move -->
    <div v-else-if="crest.order.length > 1" class="d-flex flex-wrap mt-4">
      <v-btn
        size="small"
        variant="tonal"
        prepend-icon="mdi-rotate-left"
        :disabled="disabled"
        @click="reorder(rotateOrder)"
      >
        {{ t('settings.game.crest.rotate') }}
      </v-btn>
      <v-btn
        size="small"
        variant="tonal"
        prepend-icon="mdi-swap-horizontal"
        class="ml-2"
        :disabled="disabled"
        @click="reorder(swapOrder)"
      >
        {{ t('settings.game.crest.swap') }}
      </v-btn>
    </div>
  </div>
</template>

<style scoped>
/* The badge itself: the same drawing in the preview and under each shape. */
.crest {
  display: inline-block;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
}

/* The badge as it stands next to a name: no taller than the line it sits in, so a list of players
   (the standings, an event's entries) keeps its rows at the height they always had. */
.crest--inline {
  width: 1.1em;
  height: 1.1em;
  border-radius: 0.18em;
  vertical-align: -0.15em;
}

/* The crest next to a name, on the same neutral panel as the car. */
.crest-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #26282e;
}

.crest-shapes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.crest-shape {
  padding: 4px;
  border: 3px solid transparent;
  border-radius: 10px;
  background: none;
  cursor: pointer;
  transition: border-color 0.15s, transform 0.15s;
}

.crest-shape:hover {
  transform: scale(1.08);
}

.crest-shape.selected {
  border-color: rgb(var(--v-theme-primary));
}

.crest-shape:disabled {
  cursor: default;
  opacity: 0.6;
}
</style>
